<script setup lang="ts">
import type { AssessmentValidationBlocker } from '../../model/assessments/interfaces'
import type { AssessmentValidationGroup } from '../../model/assessments/interfaces'
import type { AssessmentValidationGroupIssue } from '../../model/assessments/interfaces'
import type { AssessmentTemplateStatus } from '../../model/assessments/enums'

import { AppText } from '@shared/ui/typography'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import { useAssessmentEditorToolbar } from '../../model/assessments/assessment-editor-toolbar.hook'
import GradeSelect from '../assessments/editor/GradeSelect.vue'
import TemplateTitleInput from '../assessments/editor/TemplateTitleInput.vue'

defineOptions({
  name: 'AssessmentEditorToolbar',
})

const props = withDefaults(defineProps<{
  isDirty?: boolean
  isSaving?: boolean
  hasValidated?: boolean
  summary?: string | null
  hasProblems?: boolean
  blockers?: AssessmentValidationBlocker[]
  groups?: AssessmentValidationGroup[]
  attemptsCount?: number | null
  status?: AssessmentTemplateStatus | null
  isPublished?: boolean
  canManage?: boolean
}>(), {
  isDirty: false,
  isSaving: false,
  hasValidated: false,
  summary: null,
  hasProblems: false,
  blockers: () => [],
  groups: () => [],
  attemptsCount: null,
  status: null,
  isPublished: false,
  canManage: true,
})

const emit = defineEmits<{
  'click:save': []
  'click:validate': []
  'click:preview': []
  'click:publish': []
  'click:duplicate': []
  'click:issue': [issue: AssessmentValidationGroupIssue]
}>()

const {
  refDirtyText,
  refSaveText,
  refAttemptsLabel,
  refAttemptsHint,
  refIsSummaryError,
  refStatusLabel,
  refStatusColor,
} = useAssessmentEditorToolbar({
  isDirty: () => props.isDirty,
  isSaving: () => props.isSaving,
  hasValidated: () => props.hasValidated,
  hasProblems: () => props.hasProblems,
  attemptsCount: () => props.attemptsCount,
  status: () => props.status,
})
</script>

<template>
  <div class="assessment-editor-toolbar">
    <div class="assessment-editor-toolbar__row">
      <TemplateTitleInput />
      <GradeSelect />
      <!-- The lifecycle, shown rather than offered: it changes through Publish below and the
           list's Archive / Unarchive, never through a control here. -->
      <AppBadge
        v-if="refStatusLabel"
        :color="refStatusColor"
        :text="refStatusLabel"
      />
      <AppText
        name="body2"
        :color="isDirty ? 'red-600' : 'slate-500'"
      >
        {{ refDirtyText }}
      </AppText>
      <div class="assessment-editor-toolbar__actions">
        <!-- Save is never disabled on a never-validated document: it validates first and only
             then saves. A permanently-disabled button would be a dead end. -->
        <BtnPrimary
          :text="refSaveText"
          :disabled="isSaving"
          @click="emit('click:save')"
        />
        <BtnSecondary
          text="Validate"
          :disabled="isSaving"
          @click="emit('click:validate')"
        />
        <!-- Preview is read-only, so unlike its neighbours it stays pressable while a save is in
             flight: looking at the document cannot interfere with writing it. -->
        <BtnSecondary
          text="Preview"
          @click="emit('click:preview')"
        />
        <BtnSecondary
          text="Duplicate"
          :disabled="isSaving"
          @click="emit('click:duplicate')"
        />
        <!-- Save-and-publish in one request, behind the same validation gate as Save. Gone once
             published: the way back is the list's Archive. An archived template publishes
             straight from here — reviewing it IS being in the editor. -->
        <BtnPrimary
          v-if="canManage && !isPublished"
          text="Publish"
          color="emerald"
          :disabled="isSaving"
          @click="emit('click:publish')"
        />
      </div>
    </div>

    <!-- A row of its own only when there is a count to show — just the number, because what an
         author needs at a glance is that this template is in use at all. The sentence explaining
         why that matters is on the `title`, where it costs no width. -->
    <div
      v-if="refAttemptsLabel"
      class="assessment-editor-toolbar__row assessment-editor-toolbar__row--meta"
    >
      <AppText
        name="body2-medium"
        color="red-600"
        class="assessment-editor-toolbar__attempts"
        :title="refAttemptsHint ?? undefined"
      >
        {{ refAttemptsLabel }}
      </AppText>
    </div>

    <!-- Not gated on `summary` alone: a blocker-only document has no summary line. -->
    <div
      v-if="hasValidated && (summary || blockers.length || groups.length)"
      class="assessment-editor-toolbar__validation"
    >
      <AppText
        v-if="summary"
        name="body2-medium"
        :color="refIsSummaryError ? 'red-600' : 'emerald-600'"
      >
        {{ summary }}
      </AppText>

      <!-- One banner for everything typing cannot fix, with the next step spelled out. -->
      <div
        v-if="blockers.length"
        class="assessment-editor-toolbar__blockers"
      >
        <ul class="assessment-editor-toolbar__blocker-list">
          <li
            v-for="blocker in blockers"
            :key="blocker.label"
          >
            <AppText name="body2-medium">
              {{ blocker.label }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
            >
              {{ blocker.message }}
            </AppText>
          </li>
        </ul>
        <AppText
          name="body2"
          color="slate-500"
        >
          You can still read and edit the rest of the template. Ask engineering to fix the stored
          template before saving.
        </AppText>
      </div>

      <!-- The fixable half — each row selects the screen / question it belongs to. -->
      <ul
        v-if="groups.length"
        class="assessment-editor-toolbar__groups"
      >
        <li
          v-for="group in groups"
          :key="group.key"
          class="assessment-editor-toolbar__group"
        >
          <AppText name="body2-medium">
            {{ group.label }}
          </AppText>
          <ul class="assessment-editor-toolbar__issues">
            <li
              v-for="issue in group.issues"
              :key="`${issue.path}:${issue.message}`"
            >
              <button
                type="button"
                class="assessment-editor-toolbar__issue"
                @click="emit('click:issue', issue)"
              >
                <AppText
                  v-if="issue.field"
                  name="body2-medium"
                  color="slate-500"
                >
                  {{ issue.field }}
                </AppText>
                <AppText name="body2">
                  {{ issue.message }}
                </AppText>
              </button>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./AssessmentEditorToolbar.scss" />
